'use client';

import { Globe } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { LOCALE_REGISTRY, SUPPORTED_LOCALES, type Locale } from '@tp/shared';
import { usePathname } from '@/i18n/navigation';
import { cn } from '@/lib/cn';

interface LanguageSelectorProps {
  className?: string;
}

/**
 * Locale selector. Navigates to the same path under a new locale prefix.
 *
 * Uses `window.location.assign` (hard navigation) rather than next-intl's
 * soft `router.replace` because the `<html>` shell lives in the [locale]
 * layout — a soft locale switch re-mounts that subtree client-side, and
 * the pre-hydration `<script>` inside it (ThemeInitScript) trips React
 * 19's "script tag while rendering" dev warning. A full reload renders
 * the shell server-side only. The proxy persists the NEXT_LOCALE cookie
 * on the resulting request by syncing it to the URL's locale prefix.
 */
export function LanguageSelector({ className }: LanguageSelectorProps) {
  const current = useLocale() as Locale;
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function pick(next: Locale) {
    setOpen(false);
    if (next === current) return;
    const search = typeof window === 'undefined' ? '' : window.location.search;
    const hash = typeof window === 'undefined' ? '' : window.location.hash;
    window.location.assign(`/${next}${pathname}${search}${hash}`);
  }

  const active = LOCALE_REGISTRY[current];

  return (
    <div ref={rootRef} className={cn('relative inline-block text-left', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-muted/60 px-2.5 py-1.5',
          'text-[11px] font-medium uppercase tracking-[0.18em] text-foreground-muted',
          'transition hover:border-accent/60 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <Globe className="h-3.5 w-3.5" aria-hidden />
        <span>{active.code.toUpperCase()}</span>
      </button>

      {open ? (
        <ul
          role="listbox"
          className={cn(
            'absolute right-0 z-50 mt-1.5 min-w-[140px] overflow-hidden rounded-md border border-border bg-surface-elevated',
            'py-1 shadow-lg',
          )}
        >
          {SUPPORTED_LOCALES.map((code) => {
            const meta = LOCALE_REGISTRY[code];
            const isActive = code === current;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => pick(code)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-[12px]',
                    'transition hover:bg-surface-muted',
                    isActive ? 'text-foreground' : 'text-foreground-muted',
                  )}
                >
                  <span>{meta.nativeName}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground-subtle">
                    {code}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
